import React, { useMemo } from "react";

function getEnv(name: string, fallback: string) {
  // Vite-style env first, then runtime window override
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (import.meta as any)?.env?.[name];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = (window as any)?.__ENV__?.[name];
  return (v || w || fallback) as string;
}

function buildGrafanaExploreTraceUrl(traceId: string) {
  const base = getEnv("VITE_GRAFANA_BASE_URL", "http://localhost:3000").replace(/\/+$/, "");

  // Tempo datasource UID (confirmed: uid=tempo)
  const tempoUid = getEnv("VITE_GRAFANA_TEMPO_UID", "tempo");

  // Optional overrides (safe defaults)
  const orgIdRaw = getEnv("VITE_GRAFANA_ORG_ID", "1");
  const orgId = /^\d+$/.test(orgIdRaw) ? orgIdRaw : "1";
  const from = getEnv("VITE_GRAFANA_TRACE_RANGE_FROM", "now-1h");
  const to = getEnv("VITE_GRAFANA_TRACE_RANGE_TO", "now");

  // Grafana v11.2 deep-links traces via Explore panes with queryType="traceql"
  // and places the trace id into the "query" field.
  const panes: Record<string, any> = {
    // Pane key can be any stable id; Grafana uses random ids like "y9p"
    y9p: {
      datasource: tempoUid, // string uid (matches your working URL: "tempo")
      queries: [
        {
          refId: "A",
          datasource: { type: "tempo", uid: tempoUid },
          queryType: "traceql",
          limit: 20,
          tableType: "traces",
          query: traceId,
          // NOTE: Grafana includes filters in its own URL, but the ids are UI-generated.
          // Keeping a minimal filter shape is harmless; Grafana will ignore unknown ids.
          filters: [{ id: "traceid", operator: "=", scope: "span" }],
        },
      ],
      range: { from, to },
    },
  };

  const panesEnc = encodeURIComponent(JSON.stringify(panes));
  return `${base}/explore?schemaVersion=1&panes=${panesEnc}&orgId=${encodeURIComponent(orgId)}`;
}

export default function AdminTraceLink(props: { traceId?: string | null; className?: string }) {
  const traceId = (props.traceId || "").trim();
  const href = useMemo(() => (traceId ? buildGrafanaExploreTraceUrl(traceId) : ""), [traceId]);

  if (!traceId) {
    return (
      <div className={props.className}>
        <div className="text-xs text-gray-500">trace_id</div>
        <div className="text-sm text-gray-700">—</div>
      </div>
    );
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(traceId);
    } catch {
      // fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = traceId;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  }

  return (
    <div className={props.className}>
      <div className="text-xs text-gray-500">trace_id</div>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-xs px-2 py-1 rounded bg-gray-50 border">{traceId}</code>

        <button
          className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
          onClick={copy}
          type="button"
          title="Copy trace_id"
        >
          Copy
        </button>

        <a
          className="text-xs px-2 py-1 rounded bg-black text-white hover:opacity-90"
          href={href}
          target="_blank"
          rel="noreferrer"
          title="Open Grafana Explore (Tempo) with this trace"
        >
          Open trace
        </a>
      </div>

      <div className="text-[11px] text-gray-500 mt-1">Opens Grafana Explore → Tempo with this trace pre-filled.</div>
    </div>
  );
}